import React, { useState, useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import type { AITTheme } from '@this/shared/ui/theme';
import { getDeviceInfo, shouldShowCustomBanner, getAppBannerOnelinkUrl } from '@this/src/util/browser/device.util';
import { isBannerDismissed, setBannerDismissed } from './banner_storage';

const getStyles = (theme: AITTheme) => ({
  container: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    zIndex: theme.tokens.zIndices.toast,
    backgroundColor: theme.tokens.colors.background.primary,
    borderBottom: `1px solid ${theme.tokens.colors.border.divider}`,
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: theme.tokens.colors.text.description,
    cursor: 'pointer',
    padding: '4px',
    lineHeight: 1,
    flexShrink: 0
  },
  icon: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    flexShrink: 0
  },
  content: {
    flex: 1,
    minWidth: 0
  },
  appName: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.tokens.colors.text.primary,
    margin: 0
  },
  description: {
    fontSize: '12px',
    color: theme.tokens.colors.text.description,
    margin: '2px 0 0 0'
  },
  actionButton: {
    backgroundColor: theme.tokens.colors.brand.primary,
    color: theme.tokens.colors.common.white,
    border: 'none',
    borderRadius: '16px',
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0
  }
});

export const AppBanner: React.FC = () => {
  const theme = useTheme<AITTheme>();
  const [isVisible, setIsVisible] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<ReturnType<typeof getDeviceInfo> | null>(null);

  useEffect(() => {
    const info = getDeviceInfo();
    setDeviceInfo(info);

    if (shouldShowCustomBanner() && !isBannerDismissed() && getAppBannerOnelinkUrl()) {
      setIsVisible(true);
    }
  }, []);

  const handleClose = () => {
    if (deviceInfo) {
      const platform = deviceInfo.isIOS ? 'ios' : 'android';
      setBannerDismissed(platform);
    }
    setIsVisible(false);
  };

  const handleAction = () => {
    // ボタンタップ＝アプリへ移行した可能性が高いユーザーとみなし、バナーを無期限で非表示にする
    if (deviceInfo) {
      const platform = deviceInfo.isIOS ? 'ios' : 'android';
      setBannerDismissed(platform, true);
    }
    const url = getAppBannerOnelinkUrl();
    if (url) window.location.href = url;
  };

  if (!isVisible || !deviceInfo) return null;

  const styles = getStyles(theme);

  return (
    <div style={styles.container}>
      <button type="button" style={styles.closeButton} onClick={handleClose} aria-label="閉じる">
        ×
      </button>
      <img src="/images/apple_touch_icon/aitravel/apple-touch-icon.png" alt="AI Travel" style={styles.icon} />
      <div style={styles.content}>
        <p style={styles.appName}>AI Travel</p>
        <p style={styles.description}>アプリでより快適にご利用いただけます</p>
      </div>
      <button type="button" style={styles.actionButton} onClick={handleAction}>
        アプリで開く
      </button>
    </div>
  );
};

export default AppBanner;
